import HTTP from "owp.http";
import type { FloraOrg } from "@gss/types/Flora";
import type {
    MeasurementStat,
    OrgStructureOrg,
} from "@gss/types/measurementStat";
import type { SecurityContext } from "@gss/types/SecurityContext";
import type { DimensionScore, QuestionScore } from "@gss/types/statScore";
import type { StringRecord } from "@gss/types/StringRecord";
import type { VirtualMeasurement } from "@gss/types/VirtualMeasurement";
import type {
    AggregateOn,
    ExportResultForm,
    Option,
} from "./export/export.types";
import type {
    CountDTO,
    FreeTextResponseDTO,
    GroupedQuestionnairesPerOrg,
    RawExportOptions,
    ResultMeasurement,
    StatsPeriods,
} from "./types";

interface Export {
    uuid: string;
}

let statsService: HTTP;
let exportService: HTTP;
let floraService: HTTP;
let freeTextService: HTTP;

export const DAO = {
    init(
        serverUrl: string,
        headers: Record<string, string | undefined> = {},
    ): void {
        const restService = new HTTP(`${serverUrl}/rest`);
        statsService = restService.path("stats").options({ headers });
        exportService = restService.path("export").options({ headers });
        floraService = restService.path("flora").options({ headers });
        freeTextService = restService.path("freeText").options({ headers });
    },

    stats: {
        async getMeasurements(): Promise<ResultMeasurement[]> {
            const r = await statsService.path("measurements").get();
            return r.json<ResultMeasurement[]>();
        },
        async isPublished(
            audience: SecurityContext,
            measurementName: string,
            periods: string,
        ): Promise<boolean> {
            const r = await statsService
                .path(measurementName, "isPublished")
                .get({ params: { audience, periods } });
            return r.json<boolean>();
        },
        async getPeriods(
            audience: SecurityContext,
            measurementName: string,
        ): Promise<StatsPeriods> {
            const r = await statsService
                .path(measurementName, "periods")
                .get({ params: { audience } });
            return r.json<StatsPeriods>();
        },
        async getMeasurementStat(
            audience: SecurityContext,
            measurementName: string,
            periods: string,
        ): Promise<MeasurementStat> {
            const r = await statsService
                .path(measurementName, "measurementStat")
                .get({ params: { audience, periods } });
            return r.json<MeasurementStat>();
        },
        async getDimensionScore(
            audience: SecurityContext,
            measurementName: string,
            orgStructure: string,
            periods: string | string[],
            filters?: string[],
            orgIds?: string[],
        ): Promise<DimensionScore> {
            const r = await statsService
                .path(measurementName, orgStructure, "dimensionScore")
                .get({ params: { audience, periods, filters, orgIds } });
            return r.json<DimensionScore>();
        },
        async getDimensionScores(
            audience: SecurityContext,
            measurementName: string,
            orgStructure: string,
            periods: string | string[],
            filters?: string[],
        ): Promise<DimensionScore[]> {
            const r = await statsService
                .path(measurementName, orgStructure, "dimensionScores")
                .get({ params: { audience, periods, filters } });
            return r.json<DimensionScore[]>();
        },
        async getQuestionScore(
            audience: SecurityContext,
            measurementName: string,
            orgStructure: string,
            periods: string | string[],
            filters?: string[],
            orgIds?: string[],
        ): Promise<QuestionScore> {
            const r = await statsService
                .path(measurementName, orgStructure, "questionScore")
                .get({ params: { audience, periods, filters, orgIds } });
            return r.json<QuestionScore>();
        },
        async getQuestionScores(
            audience: SecurityContext,
            measurementName: string,
            orgStructure: string,
            periods: string | string[],
            filters?: string[],
        ): Promise<QuestionScore[]> {
            const r = await statsService
                .path(measurementName, orgStructure, "questionScores")
                .get({ params: { audience, periods, filters } });
            return r.json<QuestionScore[]>();
        },
        async getManagerOrganisations(
            measurementName: string,
            periods: string,
        ): Promise<string[]> {
            const r = await statsService
                .path(measurementName, "managerOrganisations")
                .get({ params: { periods } });
            return r.json<string[]>();
        },
        async getContactOrganisations(): Promise<string[]> {
            const r = await statsService.path("contactOrganisations").get();
            return r.json<string[]>();
        },
    },

    freeText: {
        async getOrganisations(
            measurement: string,
            period: string,
        ): Promise<OrgStructureOrg[]> {
            const r = await freeTextService
                .path(measurement, period, "organisations")
                .get();
            return r.json<OrgStructureOrg[]>();
        },
        async getCount(
            measurement: string,
            period: string,
            question: string,
            organisation: string | undefined,
            svf: string | undefined,
        ): Promise<CountDTO> {
            const params = { question, organisation, svf };
            const r = await freeTextService
                .path(measurement, period, "count")
                .get({ params });
            return r.json<CountDTO>();
        },
        async getAnswers(
            measurement: string,
            period: string,
            question: string,
            organisation: string | undefined,
            svf: string | undefined,
            limit: number,
            offset: number,
        ): Promise<FreeTextResponseDTO[]> {
            const params = { question, organisation, svf, limit, offset };
            const r = await freeTextService.path(measurement, period).get({
                params,
            });
            return r.json<FreeTextResponseDTO[]>();
        },
        async downloadExport(
            measurement: string,
            period: string,
            question: string,
            organisation: string | undefined,
            svf: string | undefined,
        ): Promise<void> {
            const params = { question, organisation, svf, limit: -1 };
            // First post options to server.
            await freeTextService
                .path(measurement, period, "export")
                .post({ params })
                .then((r) => r.json<Export>())
                .then((response) => {
                    // Then open url in new window to receive streaming response.
                    window.open(
                        `${freeTextService.getUrl()}/export/${response.uuid}`,
                    );
                });
        },
    },

    flora: {
        async getHealthcareRegions(): Promise<FloraOrg[]> {
            const r = await floraService.path("healthcareRegions").get();
            return r.json<FloraOrg[]>();
        },
        async getRegionsToHealthcareRegions(): Promise<StringRecord<string>> {
            const r = await floraService.path("healthcareRegionsMap").get();
            return r.json<StringRecord<string>>();
        },
    },

    export: {
        async getMeasurements(): Promise<VirtualMeasurement[]> {
            const r = await exportService.path("measurements").get();
            return r.json<VirtualMeasurement[]>();
        },
        async getResultForm(
            measurementName: string,
            period: string,
        ): Promise<ExportResultForm> {
            const r = await exportService
                .path("result", "form", measurementName, period)
                .get();
            return r.json<ExportResultForm>();
        },
        async getRawExportRegions(
            measurementName: string,
            period: string,
        ): Promise<Option[]> {
            const r = await exportService
                .path("result", "raw", "regions", measurementName, period)
                .get();
            return r.json<Option[]>();
        },
        async getQuestionnaireVersions(
            measurementName: string,
            period: string,
        ): Promise<GroupedQuestionnairesPerOrg[]> {
            const r = await exportService
                .path("questionnaires", measurementName, period)
                .get();
            return r.json<GroupedQuestionnairesPerOrg[]>();
        },

        /* Download using http.download() */

        async downloadAggregate(
            audience: SecurityContext,
            measurementName: string,
            period: string,
            aggregateOn: AggregateOn,
            filters: string[],
        ): Promise<void> {
            await exportService
                .path(
                    "result",
                    "aggregate",
                    measurementName,
                    period,
                    aggregateOn,
                )
                .get({
                    responseType: "blob",
                    params: { audience, filters },
                })
                .then((r) => r.download());
        },
        async downloadReport(
            measurementName: string,
            period: string,
            orgIds: string[],
            includeHistory: boolean,
            includeQuestions: boolean,
            filters: string[],
        ): Promise<void> {
            await exportService
                .path("result", "report", measurementName, period)
                .get({
                    responseType: "blob",
                    params: {
                        orgIds,
                        includeHistory,
                        includeQuestions,
                        filters,
                    },
                })
                .then((r) => r.download());
        },
        async downloadQuestionnaire(
            measurementName: string,
            period: string,
            orgId: string,
            selectionCriteria: string,
            version: string,
        ): Promise<void> {
            await exportService
                .path(
                    "questionnaires",
                    measurementName,
                    period,
                    orgId,
                    selectionCriteria,
                    version,
                )
                .get({ responseType: "blob" })
                .then((r) => r.download());
        },

        /* Download using opening a new tab */

        async downloadRawResult(options: RawExportOptions): Promise<void> {
            // First post options to server.
            await exportService
                .path("result", "raw")
                .post({ json: options })
                .then((r) => r.json<Export>())
                .then((response) => {
                    // Then open url in new window to receive streaming response.
                    window.open(
                        `${exportService.getUrl()}/result/raw/${response.uuid}`,
                    );
                });
        },
        async downloadRawRegion(
            measurementName: string,
            period: string,
            region: string,
            includeFreeText: boolean,
        ): Promise<void> {
            const params = { includeFreeText };
            // First post options to server.
            await exportService
                .path(
                    "result",
                    "raw",
                    "region",
                    measurementName,
                    period,
                    region,
                )
                .post({ params })
                .then((r) => r.json<Export>())
                .then((response) => {
                    // Then open url in new window to receive streaming response.
                    window.open(
                        `${exportService.getUrl()}/result/raw/${response.uuid}`,
                    );
                });
        },
    },
};
